import { useState, useCallback } from 'react'
import { useDataStore } from '@/lib/machine/use-data-store'
import { fetchFirmwareUpdate } from '@/lib/firmware-update/fetch-firmware-update'

export type FirmwareInstallState =
  | { phase: 'idle' }
  | { phase: 'downloading' }
  | { phase: 'sending' }
  | { phase: 'done' }
  | { phase: 'error'; message: string }

export function useFirmwareInstall() {
  const machineId = useDataStore(s => s.machineId)
  const sendMachineAction = useDataStore(s => s.sendMachineAction)
  const [state, setState] = useState<FirmwareInstallState>({ phase: 'idle' })

  const isBusy = state.phase === 'downloading' || state.phase === 'sending'

  const install = useCallback(async () => {
    if (!machineId) return

    setState({ phase: 'downloading' })

    const file = await fetchFirmwareUpdate(machineId)

    if (!file) {
      setState({
        phase: 'error',
        message: 'Failed to download firmware. Check your connection and try again.'
      })
      return
    }

    setState({ phase: 'sending' })

    sendMachineAction({
      in: 'file_write',
      file: 'fwupdate.tar.gz',
      payload: file
    })

    setState({ phase: 'done' })
  }, [machineId, sendMachineAction])

  const reset = useCallback(() => {
    if (!isBusy) setState({ phase: 'idle' })
  }, [isBusy])

  return { state, isBusy, install, reset }
}
